/* eslint-disable no-console */
/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CloseButton } from 'utils/CloseButton';
import { ModalBody } from 'utils/ModalStyle';
import { ReactComponent as ShareIcon } from 'assets/share-ios.svg';
import gql from 'graphql-tag';
import { useMutationWrapper } from 'hooks/useMutationHook';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from 'redux/selectors/auth';
import { TipoUsuarioEnum } from 'enums/tipoUsuario';

type InstallButtonProps = {
	deferredPrompt: any;
};

const Container = styled.div`
	position: fixed;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	display: flex;
	justify-content: center;
	width: 100%;
	z-index: 200; /* Asegúrate de que esté por encima de otros elementos */
	background-color: rgba(255, 255, 255, 0.8); /* Fondo blanco semi-transparente */
`;

const Button = styled.button`
	background-color: transparent;
	color: #3b82f6; /* Tailwind's blue-500 */
	transition: color 0.3s ease-in-out;
	display: block;
	width: 100%;
	max-width: 600px;
	margin: 0 auto;
	text-align: center;
	font-size: 1rem;
	border: none;
	cursor: pointer;
	text-decoration: underline;
	font-weight: 700;
	&:hover {
		color: #1d4ed8; /* Tailwind's blue-700 */
	}
`;

const Instructions = styled.div`
	color: #111827; /* Tailwind's gray-900 */
	padding: 1rem;
	border-radius: 0.5rem;
	text-align: left; /* Alinear el texto a la izquierda */
	max-width: 600px;
	margin: 0 auto;
	font-size: 1rem;

	p {
		margin-bottom: 1rem; /* Separación después del párrafo */
	}

	ol {
		list-style-type: disc; /* Usar pelotitas en lugar de guiones */
		padding-left: 1.5rem; /* Separación antes del listado */
		margin-bottom: 1rem; /* Separación después del listado */
	}
`;

export const setCookie = (name: string, value: string, days: number) => {
	const date = new Date();
	date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
	document.cookie = `${name}=${value};expires=${date.toUTCString()};path=/`;
};

export const getCookie = (name: string): string | null => {
	const nameEQ = `${name}=`;
	const cookies = document.cookie.split(';');
	// eslint-disable-next-line no-restricted-syntax
	for (const cookie of cookies) {
		const c = cookie.trim();
		if (c.startsWith(nameEQ)) return c.substring(nameEQ.length);
	}
	return null;
};

const UPSERT_USER_PWA_CHECK = gql`
	mutation UpsertRegistroPWA($user_key: uuid!, $date: timestamptz!) {
		insert_registro_pwa(
			objects: { user_key: $user_key, date: $date }
			on_conflict: { constraint: registro_pwa_user_key_key, update_columns: [date] }
		) {
			returning {
				key
				user_key
				date
			}
		}
	}
`;

const UPSERT_ESTUDIANTE_PWA_CHECK = gql`
	mutation UpsertRegistroPWAEstudiante($estudiante_key: uuid!, $date: timestamptz!) {
		insert_registro_pwa(
			objects: { estudiante_key: $estudiante_key, date: $date }
			on_conflict: { constraint: registro_pwa_estudiante_key_key, update_columns: [date] }
		) {
			returning {
				key
				estudiante_key
				date
			}
		}
	}
`;

const InstallButton: React.FC<InstallButtonProps> = ({ deferredPrompt }) => {
	const [showInstructions, setShowInstructions] = useState(false);
	const [isInstalled, setIsInstalled] = useState(!!getCookie('pwa-installed'));
	const [isLoading, setIsLoading] = useState(true);
	const [userClicked, setUserClicked] = useState(false);

	const insertRegisterPwa = useMutationWrapper(UPSERT_USER_PWA_CHECK, '', '', true);
	const insertRegisterEstudiantePwa = useMutationWrapper(UPSERT_ESTUDIANTE_PWA_CHECK, '', '', true);

	const currentUser = useSelector(selectCurrentUser);

	useEffect(() => {
		const handleAppInstalled = async () => {
			setIsInstalled(true);
			setCookie('pwa-installed', 'true', 30);
			const date = new Date().toISOString();

			if (currentUser?.tipoUsuario === TipoUsuarioEnum.estudiante) {
				await insertRegisterEstudiantePwa({
					variables: {
						estudiante_key: currentUser?.hasuraKey,
						date
					}
				});
			} else if (currentUser?.tipoUsuario && currentUser?.hasuraKey) {
				await insertRegisterPwa({
					variables: {
						user_key: currentUser.hasuraKey,
						date
					}
				});
			}
		};

		window.addEventListener('appinstalled', handleAppInstalled);

		if (isInstalled) {
			setIsInstalled(true);
		}

		const checkInstallation = () => {
			// Verificar si la app ya está instalada o si existe la cookie
			const isStandalone =
				(window.navigator as any).standalone || window.matchMedia('(display-mode: standalone)').matches;
			const hasCookie = document.cookie.includes('pwa-installed');
			const hasClicked = localStorage.getItem('pwa-clicked') === 'true'; // ✅ NUEVO

			setIsInstalled(isStandalone || hasCookie);
			setUserClicked(hasClicked); // Verificar si el usuario ya hizo clic
			setIsLoading(false); // Finaliza la carga inicial
		};
		checkInstallation();

		return () => {
			window.removeEventListener('appinstalled', handleAppInstalled);
		};
	}, []);

	const isIOS = () => {
		const userAgent = window.navigator.userAgent.toLowerCase();
		return /iphone|ipad|ipod/.test(userAgent);
	};

	const handleInstallClick = () => {
		localStorage.setItem('pwa-clicked', 'true');
		setCookie('pwa-installed', 'true', 30);
		setUserClicked(true);
		if (isIOS()) {
			setShowInstructions(true);
		} else if (deferredPrompt) {
			console.log('Prompt shown for installation'); // Log when the button is clicked
			deferredPrompt.prompt(); // Show the install prompt
			deferredPrompt.userChoice.then((choiceResult: any) => {
				if (choiceResult.outcome === 'accepted') {
					console.log('User accepted the installation');
				} else {
					console.log('User dismissed the installation');
				}
			});
		}
	};

	if (isInstalled || isLoading || userClicked) {
		return null; // Do not render the button if the app is already installed
	}

	return (
		<Container>
			{showInstructions && isIOS() ? (
				<ModalBody
					title={<></>}
					visible={showInstructions}
					footer={<>{}</>}
					closable
					destroyOnClose
					closeIcon={
						<CloseButton
							onClick={() => {
								setShowInstructions(false);
								setIsInstalled(true);
							}}
						/>
					}
				>
					<Instructions>
						<p>Para agregar esta aplicación a la pantalla de inicio:</p>
						<ol>
							<li>
								En la parte inferior de la pantalla, toca el botón de <strong>compartir</strong>{' '}
								<ShareIcon className="place-self-center my-2" />
							</li>
							<li>
								Selecciona la opción <strong>"Agregar a la pantalla de inicio"</strong>.
							</li>
						</ol>
						<p>
							¡Y listo! Ahora podrás abrir la aplicación directamente desde la pantalla de inicio como una
							app nativa.
						</p>
					</Instructions>
				</ModalBody>
			) : (
				<Button onClick={handleInstallClick} type="button">
					Crear acceso directo SWARMOB
				</Button>
			)}
		</Container>
	);
};

export const ButtonInstallButton: React.FC<InstallButtonProps> = ({ deferredPrompt }) => {
	const [showInstructions, setShowInstructions] = useState(false);
	const [isInstalled, setIsInstalled] = useState(false);
	const insertRegisterPwa = useMutationWrapper(UPSERT_USER_PWA_CHECK, '', '', true);
	const insertRegisterEstudiantePwa = useMutationWrapper(UPSERT_ESTUDIANTE_PWA_CHECK, '', '', true);

	const currentUser = useSelector(selectCurrentUser);

	useEffect(() => {
		const handleAppInstalled = () => {
			setIsInstalled(true);
			setCookie('pwa-installed', 'true', 30);
		};

		window.addEventListener('appinstalled', handleAppInstalled);

		return () => {
			window.removeEventListener('appinstalled', handleAppInstalled);
		};
	}, []);

	const isIOS = () => {
		const userAgent = window.navigator.userAgent.toLowerCase();
		return /iphone|ipad|ipod/.test(userAgent);
	};

	const handleInstallClick = async () => {
		console.log(deferredPrompt);
		if (isIOS()) {
			setCookie('pwa-installed', 'true', 30);
			setShowInstructions(true);
			const date = new Date().toISOString();

			if (currentUser?.tipoUsuario === TipoUsuarioEnum.estudiante) {
				await insertRegisterEstudiantePwa({
					variables: {
						estudiante_key: currentUser?.hasuraKey,
						date
					}
				});
			} else if (currentUser?.tipoUsuario && currentUser?.hasuraKey) {
				await insertRegisterPwa({
					variables: {
						user_key: currentUser.hasuraKey,
						date
					}
				});
			}
		} else if (deferredPrompt) {
			console.log('Prompt shown for installation'); // Log when the button is clicked
			deferredPrompt.prompt(); // Show the install prompt
			setCookie('pwa-installed', 'true', 30);
			deferredPrompt.userChoice.then(async (choiceResult: any) => {
				if (choiceResult.outcome === 'accepted') {
					console.log('User accepted the installation');
					const date = new Date().toISOString();

					if (currentUser?.tipoUsuario === TipoUsuarioEnum.estudiante) {
						await insertRegisterEstudiantePwa({
							variables: {
								estudiante_key: currentUser?.hasuraKey,
								date
							}
						});
					} else if (currentUser?.tipoUsuario && currentUser?.hasuraKey) {
						await insertRegisterPwa({
							variables: {
								user_key: currentUser.hasuraKey,
								date
							}
						});
					}
				} else {
					console.log('User dismissed the installation');
				}
			});
		}
	};

	console.log(isInstalled);

	if (isInstalled) {
		return null; // Do not render the button if the app is already installed
	}

	return (
		<div>
			{showInstructions && isIOS() ? (
				<ModalBody
					title={<></>}
					visible={showInstructions}
					footer={<>{}</>}
					closable
					destroyOnClose
					closeIcon={
						<CloseButton
							onClick={() => {
								setShowInstructions(false);
							}}
						/>
					}
				>
					<Instructions>
						<p>Para agregar esta aplicación a la pantalla de inicio:</p>
						<ol>
							<li>
								En la parte inferior de la pantalla, toca el botón de <strong>compartir</strong>{' '}
								<ShareIcon className="place-self-center my-2" />
							</li>
							<li>
								Selecciona la opción <strong>"Agregar a la pantalla de inicio"</strong>.
							</li>
						</ol>
						<p>
							¡Y listo! Ahora podrás abrir la aplicación directamente desde la pantalla de inicio como una
							app nativa.
						</p>
					</Instructions>
				</ModalBody>
			) : (
				<Button onClick={handleInstallClick} type="button">
					Crear acceso directo SWARMOB
				</Button>
			)}
		</div>
	);
};

export default InstallButton;
